import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getRequiredDocuments,
  createRequiredDocument,
  updateRequiredDocument,
} from 'services/escriturasTemplatesService';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) =>
  e && typeof e === 'object' && 'target' in e ? e.target.value : e;

const blankDoc = {
  id: null,
  name: '',
  sourceAlias: '',
  sourceUrl: '',
  isActive: true,
};
const FILTER_ALL = '__all__';

/**
 * Catálogo global de documentos requeridos (RequiredDocuments_SAAS). CRUD: el alias y
 * la URL de la fuente viven aquí una sola vez y se comparten entre todos los tipos.
 * El estado activo/inactivo se edita en el form (campo Estado). Reusa el design system
 * (Modal/Table/Button/IconButton/Badge/Input/Dropdown).
 */
export default function DocumentCatalogModal({
  DesignSystemComponents,
  t,
  onClose,
}) {
  const { Modal, Table, Button, IconButton, Badge, Input, Dropdown } =
    DesignSystemComponents ?? {};

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = lista; objeto = form (crear/editar)
  const [saving, setSaving] = useState(false);
  // Filtros + paginación client-side (el catálogo llega completo).
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const submittingRef = useRef(false);
  const abortRef = useRef(null);
  // Solo para gatear setState post-await en mutaciones: no abortamos el request
  // (un guardado/desactivación debe completar en el server aunque el usuario navegue).
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchDocs = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await getRequiredDocuments(true, controller.signal);
      setDocs(data || []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('tpl_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDocs();
    return () => abortRef.current?.abort();
  }, [fetchDocs]);

  const canSubmit = (editing?.name || '').trim().length > 0;

  const handleSave = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: editing.name.trim(),
        sourceAlias: (editing.sourceAlias || '').trim() || null,
        sourceUrl: (editing.sourceUrl || '').trim() || null,
      };
      if (editing.id) {
        // Sin signal: la mutación debe completar en el server aunque el usuario navegue.
        await updateRequiredDocument(editing.id, {
          ...payload,
          isActive: editing.isActive,
        });
      } else {
        await createRequiredDocument(payload);
      }
      if (!mountedRef.current) return;
      setEditing(null);
      await fetchDocs();
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (!mountedRef.current) return;
      const conflict = err.response?.status === 409;
      setError(
        conflict
          ? t('tpl_doc_name_conflict')
          : err.response?.data?.message || err.message || t('tpl_error_generic')
      );
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
      submittingRef.current = false;
    }
  };

  const statusBadge = (r) => {
    const text = r.isActive ? t('tpl_doc_active') : t('tpl_doc_inactive');
    const color = r.isActive ? 'success' : 'light';
    return Badge ? (
      <Badge text={text} color={color} size='sm' />
    ) : (
      <span>{text}</span>
    );
  };

  const columns = [
    { accessor: 'name', label: t('tpl_doc_col_name') },
    {
      accessor: 'sourceAlias',
      label: t('tpl_doc_col_alias'),
      render: (r) => r.sourceAlias || '—',
    },
    {
      accessor: 'sourceUrl',
      label: t('tpl_doc_col_url'),
      render: (r) =>
        r.sourceUrl ? (
          <a
            href={r.sourceUrl}
            target='_blank'
            rel='noreferrer'
            title={r.sourceUrl}
            style={{ color: 'var(--success-500, #16a34a)', fontWeight: 600 }}
          >
            ✓
          </a>
        ) : (
          '—'
        ),
    },
    { accessor: 'isActive', label: t('tpl_col_status'), render: statusBadge },
    {
      label: t('tpl_col_actions'),
      render: (r) => (
        <div className='d-flex gap-2 align-items-center'>
          {IconButton ? (
            <IconButton
              ariaLabel={t('tpl_doc_action_edit')}
              icon='pencil'
              size='sm'
              stroke='var(--primary-500)'
              onClick={() => setEditing({ ...r })}
            />
          ) : (
            <button
              type='button'
              className='btn btn-link btn-sm'
              onClick={() => setEditing({ ...r })}
            >
              {t('tpl_doc_action_edit')}
            </button>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = useMemo(
    () => [
      { label: t('tpl_filter_all_status'), value: FILTER_ALL },
      { label: t('tpl_doc_active'), value: 'active' },
      { label: t('tpl_doc_inactive'), value: 'inactive' },
    ],
    [t]
  );

  // Opciones del Dropdown de Estado del form (patrón CreateEditCourtModal): booleano
  // directo mapeado al flag `isActive`. Reusa las keys de badge ya existentes.
  const estadoOptions = useMemo(
    () => [
      { label: t('tpl_doc_active'), value: true },
      { label: t('tpl_doc_inactive'), value: false },
    ],
    [t]
  );

  const visibleDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (q && !`${d.name} ${d.sourceAlias || ''}`.toLowerCase().includes(q))
        return false;
      if (statusFilter === 'active' && !d.isActive) return false;
      if (statusFilter === 'inactive' && d.isActive) return false;
      return true;
    });
  }, [docs, search, statusFilter]);

  const listView = (
    <div>
      {error && (
        <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
          {error}
        </div>
      )}
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <p className='caption font-light mb-0'>{t('tpl_doc_subtitle')}</p>
        {Button && (
          <Button
            label={t('tpl_doc_new')}
            size='sm'
            variant='primary'
            onClick={() => setEditing({ ...blankDoc })}
          />
        )}
      </div>

      {/* Filtros: búsqueda (nombre/fuente) + estado */}
      <div className='d-flex flex-wrap gap-2 mb-3'>
        {Input && (
          <div style={{ flex: '1 1 240px' }}>
            <Input
              icon='search'
              placeholder={t('tpl_doc_search_placeholder')}
              value={search}
              onChange={(e) => {
                setSearch(readVal(e));
                setPage(0);
              }}
              width='100%'
            />
          </div>
        )}
        {Dropdown && (
          <div style={{ flex: '0 1 200px' }}>
            <Dropdown
              options={statusOptions}
              value={
                statusOptions.find((o) => o.value === statusFilter) ||
                statusOptions[0]
              }
              onChange={(o) => {
                setStatusFilter(o?.value ?? FILTER_ALL);
                setPage(0);
              }}
              width='100%'
            />
          </div>
        )}
      </div>

      {Table ? (
        <Table
          columns={columns}
          data={visibleDocs}
          loading={loading}
          paginator
          paginationMode='client'
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(0);
          }}
          totalItems={visibleDocs.length}
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
          ]}
        />
      ) : (
        <p className='font-light'>{loading ? t('tpl_loading') : null}</p>
      )}
      {!loading && visibleDocs.length === 0 && (
        <div className='text-center p-3'>
          <p className='caption font-light'>
            {search.trim() || statusFilter !== FILTER_ALL
              ? t('tpl_typedocs_search_empty')
              : t('tpl_doc_empty')}
          </p>
        </div>
      )}
      <div className='d-flex justify-content-end mt-3'>
        {Button && (
          <Button
            label={t('tpl_btn_close')}
            size='sm'
            variant='secondary'
            onClick={onClose}
          />
        )}
      </div>
    </div>
  );

  const formView = editing && (
    <div>
      {error && (
        <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
          {error}
        </div>
      )}
      {Input ? (
        <div className='d-flex flex-column gap-3'>
          <Input
            label={t('tpl_doc_field_name')}
            value={editing.name}
            onChange={(e) => setEditing((s) => ({ ...s, name: readVal(e) }))}
            disabled={saving}
            width='100%'
          />
          <Input
            label={t('tpl_doc_field_alias')}
            value={editing.sourceAlias}
            onChange={(e) =>
              setEditing((s) => ({ ...s, sourceAlias: readVal(e) }))
            }
            disabled={saving}
            placeholder='CBR, SRCeI, Registro Civil…'
            width='100%'
          />
          <Input
            label={t('tpl_doc_field_url')}
            value={editing.sourceUrl}
            onChange={(e) =>
              setEditing((s) => ({ ...s, sourceUrl: readVal(e) }))
            }
            disabled={saving}
            placeholder='https://…'
            width='100%'
          />
          {editing.id && Dropdown && (
            <div>
              <label className='form-label'>{t('tpl_estado_label')}</label>
              <Dropdown
                options={estadoOptions}
                value={
                  estadoOptions.find((o) => o.value === !!editing.isActive) ??
                  estadoOptions[0]
                }
                onChange={(o) =>
                  setEditing((s) => ({ ...s, isActive: o?.value ?? true }))
                }
                width='100%'
              />
            </div>
          )}
        </div>
      ) : (
        <p className='font-light'>Cargando componentes…</p>
      )}
      <div className='d-flex justify-content-end gap-2 mt-3'>
        {Button && (
          <Button
            label={t('tpl_btn_cancel')}
            size='sm'
            variant='secondary'
            onClick={() => {
              setEditing(null);
              setError(null);
            }}
            disabled={saving}
          />
        )}
        {Button && (
          <Button
            label={saving ? t('tpl_loading') : t('tpl_type_save')}
            size='sm'
            variant='primary'
            onClick={handleSave}
            disabled={!canSubmit || saving}
          />
        )}
      </div>
    </div>
  );

  const title = editing
    ? editing.id
      ? t('tpl_doc_edit_title')
      : t('tpl_doc_create_title')
    : t('tpl_doc_title');
  const body = editing ? formView : listView;

  return (
    <>
      {Modal ? (
        <Modal
          title={title}
          show
          onClose={onClose}
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
          width='760px'
        >
          {body}
        </Modal>
      ) : (
        <div
          role='dialog'
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            zIndex: 9500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
          }}
          onClick={onClose}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 20,
              width: 'min(760px, 94vw)',
            }}
          >
            <h5>{title}</h5>
            {body}
          </div>
        </div>
      )}
    </>
  );
}
